// src/context/ThemeContext.js
// Thème avec mode clair/sombre ET couleur d'accent sélectionnable - NE PAS MODIFIER

import React, { createContext, useState, useEffect, useCallback } from 'react';

export const ThemeContext = createContext();

// Palette de couleurs disponibles - chacune a une version claire (fond blanc) et sombre (fond navy)
export const THEME_COLORS = {
  green: { label: 'Vert', light: '#2F7A52', dark: '#4C9A6A' },
  red: { label: 'Rouge', light: '#B91C1C', dark: '#E05252' },
  blue: { label: 'Bleu', light: '#1D4ED8', dark: '#4B8CE0' },
  gold: { label: 'Jaune / Or', light: '#B8860B', dark: '#E0B23A' },
  purple: { label: 'Violet', light: '#7C3AED', dark: '#A78BFA' },
};

const buildTheme = (mode, colorKey) => {
  const accent = THEME_COLORS[colorKey] || THEME_COLORS.green;
  const accentHex = mode === 'light' ? accent.light : accent.dark;

  // Conversion hex → rgba pour les variantes soft/border, sans dépendance externe
  const hexToRgba = (hex, alpha) => {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  if (mode === 'light') {
    return {
      mode: 'light',
      bg: '#FFFFFF', bgCard: '#FAFBFA', bgInput: '#F2F4F2', bgElevated: '#FFFFFF',
      textPrimary: '#0F1419', textSecondary: '#5A6472', textMuted: '#9AA3AF',
      gold: accentHex,
      goldSoft: hexToRgba(accentHex, 0.08),
      goldBorder: hexToRgba(accentHex, 0.25),
      border: 'rgba(0, 0, 0, 0.08)', borderSoft: 'rgba(0, 0, 0, 0.05)',
      navBg: '#FFFFFF', overlay: 'rgba(0, 0, 0, 0.5)', shadow: '0 10px 25px rgba(0, 0, 0, 0.10)',
    };
  }

  return {
    mode: 'dark',
    bg: '#0A0E1A', bgCard: '#162033', bgInput: '#111827', bgElevated: '#1C2537',
    textPrimary: '#FFFFFF', textSecondary: '#A0AEC0', textMuted: '#4A5568',
    gold: accentHex,
    goldSoft: hexToRgba(accentHex, 0.15),
    goldBorder: hexToRgba(accentHex, 0.3),
    border: 'rgba(255, 255, 255, 0.1)', borderSoft: 'rgba(255, 255, 255, 0.05)',
    navBg: '#0A0E1A', overlay: 'rgba(0, 0, 0, 0.6)', shadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
  };
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light');
  const [colorKey, setColorKey] = useState('green');

  useEffect(() => {
    const savedMode = localStorage.getItem('fynho_theme');
    if (savedMode === 'light' || savedMode === 'dark') setMode(savedMode);

    const savedColor = localStorage.getItem('fynho_theme_color');
    if (savedColor && THEME_COLORS[savedColor]) setColorKey(savedColor);
  }, []);

  useEffect(() => {
    const theme = buildTheme(mode, colorKey);
    document.body.style.backgroundColor = theme.bg;
    document.body.style.color = theme.textPrimary;
    localStorage.setItem('fynho_theme', mode);
    localStorage.setItem('fynho_theme_color', colorKey);
  }, [mode, colorKey]);

  const toggleTheme = useCallback(() => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setThemeColor = useCallback((key) => {
    if (THEME_COLORS[key]) setColorKey(key);
  }, []);

  const theme = buildTheme(mode, colorKey);

  const value = {
    theme,
    mode,
    toggleTheme,
    isDark: mode === 'dark',
    isLight: mode === 'light',
    colorKey,
    setThemeColor,
    availableColors: THEME_COLORS,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};